import React, { useCallback, useMemo, useState } from 'react';
import { type FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Box, Button, Stack, Typography, useTheme } from '@mui/material';
import _omit from 'lodash/omit';
import { ArrayParam, NumberParam, StringParam, withDefault } from 'serialize-query-params';
import { useQueryParams } from 'use-query-params';
import { useToggle } from 'usehooks-ts';

import {
  LatestProcessCriteriaDTOTalentStatus,
  TalentCreateDTOType,
  type TalentListRequestDTO,
  TalentResponseDTO,
  useAutosuggestTalentsHook,
  useFilterTalentsInfinite
} from '@/api/generated';
import PlusIcon from '@/assets/icons/plus.svg?react';
import DataGrid from '@/components/DataGrid/DataGrid';
import { AutocompleteWide } from '@/components/fields/Autocomplete';
import Form from '@/components/Form/Form';
import StickyHead from '@/components/StickyHead/StickyHead';
import { useIsMidotUser } from '@/hooks/permissions/useIsMidotUser';
import { DEFAULT_PAGINATION, Pagination, Sorting, useEntityList } from '@/hooks/useEntityList';
import { usePageTitle } from '@/hooks/usePageTitle';
import {
  CREATE_MODAL_PARAM,
  DEFAULT_LOCATION_SLUG,
  DEFAULT_ORGANIZATION_SLUG,
  DEFAULT_TYPE,
  toTalent
} from '@/root/linker';
import { getUserFullName } from '@/utils/user';
import { ProcessSectionHiddenFields, TalentSectionHiddenFields } from '@/views/Filters/constants';
import FilterButton from '@/views/Filters/FilterButton';
import FilterPanel from '@/views/Filters/FilterPanel';
import SectionProcess from '@/views/Filters/SectionProcess';
import SectionTalent from '@/views/Filters/SectionTalent';

import CreateTalentModal from './CreateTalentModal';
import { talentColumns } from './talentColumns';
import TalentStatusFilter from './TalentStatusFilter';

type TalentParams = TalentListRequestDTO & Pagination & Sorting;

const URL_CONFIG_MAP = {
  [DEFAULT_TYPE]: StringParam,
  [DEFAULT_ORGANIZATION_SLUG]: StringParam,
  [DEFAULT_LOCATION_SLUG]: StringParam
};

const Talents = () => {
  const { t } = useTranslation(['talents', 'filters']);
  const { spacing } = useTheme();
  const [showFilters, toggleShowFilters] = useToggle();
  const [searchParams] = useSearchParams();
  const [showCreateTalentModal, toggleShowCreateTalentModal] = useToggle(!!searchParams.get(CREATE_MODAL_PARAM));
  usePageTitle(t('layouts.talents.title'));

  const [queryBody, setQueryBody] = useState<TalentParams | null>(null);

  const [queryParams] = useQueryParams(URL_CONFIG_MAP);

  const defaultType: TalentCreateDTOType | undefined = queryParams[DEFAULT_TYPE] as TalentCreateDTOType | undefined;
  const defaultOrganizationSlug: string | undefined = queryParams[DEFAULT_ORGANIZATION_SLUG] ?? undefined;
  const defaultLocationSlug: string | undefined = queryParams[DEFAULT_LOCATION_SLUG] ?? undefined;

  const autosuggestTalents = useAutosuggestTalentsHook();

  // FIXME: Remove the suspense: false once suspense is false by default
  const {
    data: talentsData,
    isLoading,
    isError,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage
  } = useFilterTalentsInfinite(
    queryBody ?? {},
    { includeStats: true },
    {
      query: { suspense: false, enabled: !!queryBody }
    }
  );

  const handleFetchNextPage = useCallback(() => {
    if (isError || isFetchingNextPage || !hasNextPage || !talentsData?.pages?.length) return;
    fetchNextPage();
  }, [fetchNextPage, hasNextPage, isError, isFetchingNextPage, talentsData?.pages?.length]);

  const talentsList = useMemo(
    () => (talentsData?.pages?.flatMap(page => page?.talents) || []) as TalentResponseDTO[],
    [talentsData]
  );

  const omitParams = useCallback((queryBody: TalentParams) => {
    let result: TalentParams = queryBody;
    if (queryBody.talents?.latestProcess?.talentStatus === LatestProcessCriteriaDTOTalentStatus.GHOST) {
      result = {
        ...queryBody,
        talents: {
          ...queryBody.talents,
          latestProcess: {
            ...queryBody.talents?.latestProcess,
            talentStatus: '__NULL__' as LatestProcessCriteriaDTOTalentStatus
          }
        }
      };
    }

    return _omit(result, [CREATE_MODAL_PARAM, DEFAULT_TYPE, DEFAULT_ORGANIZATION_SLUG, DEFAULT_LOCATION_SLUG]);
  }, []);

  const { form, handleChangeSort, sorting } = useEntityList<TalentParams, TalentParams>(
    DEFAULT_VALUES,
    MAP_CONFIG,
    setQueryBody,
    omitParams,
    300
  );

  const handleSearchTalents = useCallback(
    async (search: string) => {
      try {
        const searchResults = await autosuggestTalents({ search });

        return searchResults.map(({ id, firstName, lastName }) => ({
          label: getUserFullName({ firstName, lastName }),
          value: getUserFullName({ firstName, lastName }),
          to: toTalent({ talentId: id! })
        }));
      } catch (e) {
        return [];
      }
    },
    [autosuggestTalents]
  );

  const handleOpenCreateModal = useCallback(() => {
    toggleShowCreateTalentModal();
  }, [toggleShowCreateTalentModal]);

  const { isMidotUser } = useIsMidotUser();
  const talentHiddenFilters = useMemo(() => getTalentHiddenFilters(isMidotUser), [isMidotUser]);

  return (
    <Box sx={{ pb: 5, px: 5, flexGrow: 1 }} component="div">
      <StickyHead sx={{ paddingTop: spacing(2) }}>
        <Stack direction="row" justifyContent="start">
          <Typography component="h1" variant="h2" mb={3}>
            {t('layouts.talents.title')}
          </Typography>
        </Stack>

        <TalentStatusFilter form={form} />

        <Form form={form}>
          <Stack direction="row" sx={{ marginTop: spacing(3) }}>
            <AutocompleteWide placeholder={t('layouts.talents.autocomplete')} loadOptions={handleSearchTalents} />

            <Stack direction="row" sx={{ marginInlineStart: 'auto' }}>
              <FilterButton isOpen={showFilters} onClick={toggleShowFilters} />
              <Button
                variant="contained"
                startIcon={<PlusIcon />}
                onClick={handleOpenCreateModal}
                sx={{ marginInlineStart: 3 }}
              >
                {t('filters:filters.new')}
              </Button>
              {showCreateTalentModal && (
                <CreateTalentModal
                  onClose={toggleShowCreateTalentModal}
                  defaultType={defaultType}
                  defaultOrganizationSlug={defaultOrganizationSlug}
                  defaultLocationSlug={defaultLocationSlug}
                />
              )}
            </Stack>
          </Stack>

          <FilterPanel isOpen={showFilters}>
            <SectionTalent hiddenFields={talentHiddenFilters} name="talents" />
            <SectionProcess hiddenFields={processHiddenFilters} name="process" />
          </FilterPanel>
        </Form>
      </StickyHead>

      <DataGrid
        data={talentsList}
        columns={talentColumns(t)}
        isLoading={isLoading || !queryBody}
        isLoadingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage}
        onSort={handleChangeSort}
        sorting={sorting}
        resizeWatchSelector=".FiltersPanel-root"
        hasError={isError}
        onLoadNextPage={handleFetchNextPage}
      />
    </Box>
  );
};

export default Talents;

const DEFAULT_VALUES: TalentParams & FieldValues = {
  ...DEFAULT_PAGINATION,
  talents: {
    searchByName: '',
    latestProcess: {
      talentStatus: undefined
    }
  },
  process: {
    search: ''
  }
};

const MAP_CONFIG = {
  'talent.createdBy': ArrayParam,
  size: withDefault(NumberParam, DEFAULT_PAGINATION.size)
};

const getTalentHiddenFilters = (isMidotUser: boolean) => {
  const hiddenFields = [
    TalentSectionHiddenFields.OVERALL_SCORE,
    TalentSectionHiddenFields.HIRING_DECISION,
    TalentSectionHiddenFields.KAM,
    TalentSectionHiddenFields.LIMITED_TALENT
  ];

  if (!isMidotUser) {
    hiddenFields.push(TalentSectionHiddenFields.OFFICE);
  }

  return new Set(hiddenFields);
};

const processHiddenFilters = new Set([
  ProcessSectionHiddenFields.OFFICE,
  ProcessSectionHiddenFields.ORGANIZATION,
  ProcessSectionHiddenFields.LABELS,
  ProcessSectionHiddenFields.FEATURES,
  ProcessSectionHiddenFields.TALENTS_COUNT,
  ProcessSectionHiddenFields.CREATED_BY,
  ProcessSectionHiddenFields.KAM
]);
